import React from "react";
import { useCrmMode } from "store/selectors/hooks";
import { useTenantSettings } from "store/selectors/settings";
import { CrmMode } from "util/crm-mode";

export type ClientNameOrder = "clientFirst" | "bestContactFirst";

interface ClientNameOrderParams {
    crmMode: CrmMode;
    showClientFirst: boolean;
}

export const clientNameOrder = (params: ClientNameOrderParams): ClientNameOrder => {
    switch (params.crmMode) {
        case "placement-only":
        case "combined":
            return params.showClientFirst ? "clientFirst" : "bestContactFirst";
        case "home-care-only":
        case "none":
            return "clientFirst";
        default: {
            const exhaustiveCheck: never = params.crmMode;
            throw new Error(`Unhandled CRM mode ${String(exhaustiveCheck)}`);
        }
    }
};

export const useClientNameOrder = (): ClientNameOrder => {
    const { mode } = useCrmMode();
    const { settings } = useTenantSettings();

    return clientNameOrder({ crmMode: mode, showClientFirst: settings.showClientFirst });
};

interface ClientAndBestContactProps<T> {
    order: ClientNameOrder;
    client: T;
    bestContact: T;
}

export const orderClientAndBestContact = <T,>(
    params: ClientAndBestContactProps<T>,
): [T, T] =>
    params.order === "clientFirst"
        ? [params.client, params.bestContact]
        : [params.bestContact, params.client];

export const ClientAndBestContact: React.FC<
    ClientAndBestContactProps<React.ReactNode>
> = props => {
    const [first, second] = orderClientAndBestContact(props);

    return (
        <>
            {first}
            {second}
        </>
    );
};
